import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
} from 'react';
import equal from 'fast-deep-equal';

import {ACTIONS} from './constants';
import reducer, {initialState, Cells} from './reducer';
import {inSingleColumn, inSingleRow} from './utils';
import {HypergridCellEvent} from '../../types';

import PointsWebWorker from '@netx/core-grid/lib/webworkers/selection_points';
import {getInlineWorker} from '@netx/core/lib/underscore-netx';

import {getColumnsByName} from '../../utils';

type StoreContextType = {
	// dispatch: React.Dispatch<any>;
	state: typeof initialState;

	getPoints(): Promise<Uint16Array>;
	reset(): void;
	selectAll(): void;
};

const StoreContext = createContext<StoreContextType>({} as any);

export const useSelectionStore = () =>
	useContext<StoreContextType>(StoreContext);

export const SelectionStoreProvider = ({
	children,
	gridEditor,
}: React.PropsWithChildren<any>) => {
	// TODO: Ideally what we would do with all this data is start compressing selection down
	// as we check for loose cells - if someone loosely selects an entire column or row, remove
	// the selection from the loose data and add it it columns/rows as necessary
	// But hypergrid stores selections as custom rects that represent start.y, end.y, start.x, end.x
	// so we would need to tap deeper into hypergrid to recreate these rectangles as needed - ultimately
	// it proves to take a lot of work for something that may not happen and if it does, the chances
	// the user is dealing with a small data set is very high
	const [state, dispatch] = useReducer(reducer, initialState);

	const _inSingleColumn = inSingleColumn(gridEditor);
	const _inSingleRow = inSingleRow(gridEditor);

	const [progress, setProgress] = React.useState<number | null>(null);
	const timeoutRef = React.useRef<NodeJS.Timeout>(null);

	const handleSelectionChangeCheck = useCallback(
		(event: HypergridCellEvent) => {
			if (gridEditor.selectionModel.allRowsSelected) {
				dispatch({type: ACTIONS.SET_ALL, all: true});
				return false;
			}
			return true;
		},
		[gridEditor],
	);

	const handleColumnSelectionChange = useCallback(
		async (event: HypergridCellEvent) => {
			if (!handleSelectionChangeCheck(event)) return;

			const {detail} = event;
			const {columns} = gridEditor.behavior;

			// No need to update (not sure why hypergrid doesn't check this)
			const columnNames = detail.columns.map((column) => columns[column]?.name);
			if (
				columnNames.length === 1 &&
				state.columns.length === 1 &&
				columnNames[0] === state.columns[0]
			) {
				return;
			}

			dispatch({
				type: ACTIONS.SET_COLUMNS,
				columns: columnNames,
				hasRows: !!detail.rows.length,
				hasSelections: !!detail.selections.length,
			});
		},
		[handleSelectionChangeCheck, state.columns],
	);

	const handleRowSelectionChange = useCallback(
		async (event: HypergridCellEvent) => {
			if (!handleSelectionChangeCheck(event)) return;

			const {detail} = event;

			// No need to update (not sure why hypergrid doesn't check this)
			if (
				detail.rows.length === 1 &&
				state.rows.length === 1 &&
				detail.rows[0] === state.rows[0]
			) {
				return;
			}

			dispatch({
				type: ACTIONS.SET_ROWS,
				hasColumns: !!detail.columns.length,
				hasSelections: !!detail.selections.length,
				rows: detail.rows,
			});
		},
		[handleSelectionChangeCheck, state.rows],
	);

	const handleSelectionChange = useCallback(
		async (event: HypergridCellEvent) => {
			if (!handleSelectionChangeCheck(event)) return;

			const {detail} = event;

			const selections = gridEditor.getSelections();
			const selectionCount = selections.length;

			const cells: Cells = {};

			// No sense in doing any of this if the grid isn't even claiming a selection
			if (!selectionCount) {
				dispatch({
					type: ACTIONS.SET_CELLS,
					cells,
					hasRows: !!detail.rows.length,
					hasColumns: !!detail.columns.length,
				});
				return;
			}

			const columnCount = gridEditor.numColumns;
			const rowCount = gridEditor.numRows;

			// Unfortuantely it will trigger this event for out of bound cells (cells that you may not think exist)
			// If I recall correctly this was on their radar but someone said that clicking in out of bound area
			// was to signify you want to add columns or rows - it would be nice if they just dispatched a different event
			// but such is life.
			for (let i = 0; i < selectionCount; i++) {
				const rect = selections[i];
				let endX = rect.right;
				let endY = rect.bottom;
				const startX = rect.left;
				let startY = rect.top;
				let xSelected = 0;
				let ySelected = 0;

				// Out of bounds - 0 cells are selected for this rect
				if (startX >= columnCount || endY < 0) return;

				//////////////////////////////
				// Y AXIS
				//////////////////////////////
				// If filters are on, this is absolutely necessary
				// The column header being clicked causes an entire column to be selected and a sepearate event
				// Adjust start y
				if (startY < 0) startY = 0;
				// Adjust end y
				if (endY >= rowCount) endY = rowCount - 1;
				// Our selection on the y axis
				ySelected = endY - startY;
				// 0 cells selected for this rect
				if (ySelected < 0) return;
				//////////////////////////////
				// !Y AXIS
				//////////////////////////////

				//////////////////////////////
				// X AXIS
				//////////////////////////////
				// Clicking the row header (which would normally be out of bounds for the x axis)
				// causes an entire row selection and a spearate event - so we only need to worry about the right side bounds
				// Adjust end x
				if (endX >= columnCount) endX = columnCount - 1;
				// Our selection on the x axis
				xSelected = endX - startX;
				// 0 cells selected for this rect
				if (xSelected < 0) return;
				//////////////////////////////
				// !X AXIS
				//////////////////////////////

				// DO NOT DO THIS- YOU HAVE NO IDEA THE SIZE OF AN ARRAY YOU MIGHT CREATE
				// Store points
				// for( var x = startX; x <= endX; x++ ) {
				// 	for( var y = startY; y <= endY; y++ ) {
				// 		var point = { x: x, y: y };
				// 		if( !_.findWhere( self.cells, point ) ) {
				// 			self.cells.push( point );
				// 		}
				// 	}
				// }

				// Instead- let's just track the rows and columns and not each individual point
				// TODO: what would be nice is if we could determine whole row / column selections, add them to the selectedRows / selectedColumns array and remove them from
				// the selection. Unfortunately hypergrid deals in rectangles with bounds - so it gets complicated - you would need to go through them
				// all, recreate the rectangle without the row / column. This would be preferred - but not something I want to spend the time on right now
				// as it could utlimately result in just as much (if not more) lag
				// The important thing here is that even though we are storing (potentially) a lot of data - the calculations to determine selections are better
				// and will not have to parse over each point (in most cases)
				for (let y = startY; y <= endY; y++) {
					if (cells[y] === void 0) cells[y] = [];
					for (let x = startX; x <= endX; x++) {
						if (cells[y].indexOf(x) < 0) cells[y].push(x);
					}
				}
			}

			if (state.count === 1 && equal(cells, state.cells)) return;

			dispatch({
				type: ACTIONS.SET_CELLS,
				cells,
				hasRows: !!detail.rows.length,
				hasColumns: !!detail.columns.length,
			});
		},
		[handleSelectionChangeCheck, state.count, state.cells],
	);

	// Hook up listeners
	useEffect(() => {
		if (!gridEditor) return;

		gridEditor.addEventListener(
			'fin-column-selection-changed',
			handleColumnSelectionChange,
		);
		gridEditor.addEventListener('fin-selection-changed', handleSelectionChange);
		gridEditor.addEventListener(
			'fin-row-selection-changed',
			handleRowSelectionChange,
		);
		return () => {
			try {
				gridEditor.removeEventListener(
					'fin-column-selection-changed',
					handleColumnSelectionChange,
				);
				gridEditor.removeEventListener(
					'fin-selection-changed',
					handleSelectionChange,
				);
				gridEditor.removeEventListener(
					'fin-row-selection-changed',
					handleRowSelectionChange,
				);
			} catch (ex) {}
		};
	}, [
		handleColumnSelectionChange,
		handleSelectionChange,
		handleRowSelectionChange,
	]);

	useEffect(() => {
		timeoutRef.current && clearTimeout(timeoutRef.current);

		if (!gridEditor) return;

		timeoutRef.current = setTimeout(() => {
			const singleColumn = _inSingleColumn(state);
			const singleRow = _inSingleRow(state);

			const columns = gridEditor.getColumns();

			const {numColumns, numRows} = gridEditor;

			let count = 0;
			if (state.all) {
				count = numRows * numColumns;
			} else {
				const cells = state.cells;
				let cellCount = 0;

				// Do not include cells within selected columns/rows
				for (const key in cells) {
					if (!cells.hasOwnProperty(key)) continue;

					const row = parseInt(key);
					if (state.rows.includes(row)) continue;

					const cols = cells[key];
					for (let i = 0, c = cols.length; i < c; i++) {
						const column = columns[cols[i]];
						if (!column || !state.columns.includes(column.name)) cellCount++;
					}
				}
				// Total cells that will be collected
				count =
					cellCount +
					state.columnCount * numRows +
					state.rowCount * numColumns -
					state.columnCount * state.rowCount;
			}

			dispatch({type: ACTIONS.SET_COUNT, count, singleColumn, singleRow});
		}, 50);
	}, [state.all, state.cells, state.columns, state.rows, gridEditor]);

	const reset = useCallback(() => {
		dispatch({type: ACTIONS.RESET});
		gridEditor.selectionModel.clear();
		gridEditor.repaint();
	}, [gridEditor]);

	const selectAll = useCallback(() => {
		if (state.all) return;
		gridEditor.toggleSelectAllRows();
	}, [state.all, gridEditor]);

	const getPoints = useCallback((): Promise<Uint16Array> => {
		// 10,000 rows at 1,000 columns (10,000,000 points) takes 56ms
		// Which is 3.5 times longer than the refresh rete (16ms)
		// Using a web worker increaes this delay to 200ms but does not cause any
		// hesitation in the browser

		// 30,000 rows at 250  columns (7,500,000  points) 298ms
		// 20,000 rows at 500  columns (10,000,000 points) 264ms
		// 15,000 rows at 1000 columns (15,000,000 points) 1.378s

		const columnsByName = getColumnsByName(gridEditor.behavior.columns);

		return new Promise((resolve, reject) => {
			const worker = getInlineWorker(PointsWebWorker);
			// @ts-ignore
			worker.onmessage = _.throttle(
				function (e: any) {
					const {data} = e;
					setProgress(data.progress);
					if (data.progress === 100) {
						setProgress(100);
						resolve(data.points);
					}
				},
				16,
				{trailing: true},
			);

			worker.postMessage({
				all: state.all,
				cells: state.cells,
				columns: state.columns.map(
					(columnName) => columnsByName[columnName]?._index,
				),
				rows: state.rows,
				totalColumns: gridEditor.numColumns,
				totalRows: gridEditor.numRows,
			});
		});
	}, [state.all, state.cells, state.columns, state.rows, gridEditor]);

	return (
		<StoreContext.Provider
			value={{
				state,

				getPoints,
				reset,
				selectAll,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};
